import { Track } from "@/src/lib/tracking";

/**
 * OHLC数据结构
 */
export interface OHLCData {
  /** 时间戳 */
  time: number;
  /** 开盘价 */
  open: number;
  /** 最高价 */
  high: number;
  /** 最低价 */
  low: number;
  /** 收盘价 */
  close: number;
}

/**
 * 趋势分析结果
 */
export interface TrendAnalysisResult {
  /** 趋势类型: 'uptrend' | 'downtrend' */
  trendType: "uptrend" | "downtrend";
  /** 连续趋势的最早一天时间戳 */
  startTime: number;
  /** 连续趋势的最早一天OHLC数据 */
  startOHLC: OHLCData;
  /** 连续趋势期间的总变化幅度（百分比） */
  totalChangePercent: number;
  /** 连续趋势的天数 */
  trendDays: number;
  /** 起始价格 */
  startPrice: number;
  /** 结束价格 */
  endPrice: number;
}

/**
 * 趋势分析计算函数
 *
 * @param ohlcData - OHLC数据数组，按时间正序排列（最早的在前，最新的在后）
 * @param maxDays - 最大分析天数，默认30天
 * @returns 趋势分析结果
 *
 * @example
 * ```typescript
 * const ohlcData = [
 *   { time: 1640736000000, open: 95, high: 100, low: 93, close: 100 },   // 最早
 *   { time: 1640822400000, open: 100, high: 105, low: 98, close: 105 },
 *   { time: 1640908800000, open: 105, high: 110, low: 103, close: 110 },
 *   { time: 1640995200000, open: 110, high: 115, low: 108, close: 112 }, // 最新
 * ];
 *
 * const result = trendAnalysisCalc(ohlcData);
 * console.log(result);
 * // {
 * //   trendType: 'uptrend',
 * //   startTime: 1640736000000,
 * //   startOHLC: { time: 1640736000000, open: 95, high: 100, low: 93, close: 100 },
 * //   totalChangePercent: 12,
 * //   trendDays: 4,
 * //   startPrice: 100,
 * //   endPrice: 112
 * // }
 * ```
 */
export const trendAnalysisCalc = (
  ohlcData: OHLCData[],
  maxDays: number = 30,
): TrendAnalysisResult => {
  // 参数验证
  if (!Array.isArray(ohlcData) || ohlcData.length < 2) {
    // throw new Error('OHLC数据必须是非空数组且至少包含2个数据点');
    Track.log(
      "error",
      "trendAnalysisCalc",
      "OHLC数据必须是非空数组且至少包含2个数据点",
    );
    return {
      trendType: "uptrend",
      startTime: 0,
      startOHLC: { time: 0, open: 0, high: 0, low: 0, close: 0 },
      totalChangePercent: 0,
      trendDays: 0,
      startPrice: 0,
      endPrice: 0,
    };
  }

  if (maxDays < 2) {
    Track.log(
      "error",
      "trendAnalysisCalc",
      "OHLC数据必须是非空数组且至少包含2个数据点",
    );
    // throw new Error('最大分析天数必须大于等于2');
    return {
      trendType: "uptrend",
      startTime: 0,
      startOHLC: { time: 0, open: 0, high: 0, low: 0, close: 0 },
      totalChangePercent: 0,
      trendDays: 0,
      startPrice: 0,
      endPrice: 0,
    };
  }

  // 限制分析天数（从最新的数据开始，向前取指定天数）
  const dataToAnalyze = ohlcData.slice(-Math.min(maxDays, ohlcData.length));

  // 获取最新一天的数据（数组最后一个元素）
  const latestData = dataToAnalyze[dataToAnalyze.length - 1];
  const previousData = dataToAnalyze[dataToAnalyze.length - 2];

  // 判断当前趋势：比较最新一天与前一天的价格
  const currentClose = latestData.close;
  const previousClose = previousData.close;

  // 确定趋势类型
  const isUptrend = currentClose > previousClose;
  const trendType: "uptrend" | "downtrend" = isUptrend
    ? "uptrend"
    : "downtrend";

  // 查找连续趋势的起始点
  let startIndex = dataToAnalyze.length - 1;
  let consecutiveCount = 0;

  // 从倒数第二个数据点开始向前遍历（因为最后一个是最新的）
  for (let i = dataToAnalyze.length - 2; i >= 0; i--) {
    const current = dataToAnalyze[i];
    const next = dataToAnalyze[i + 1];

    const isCurrentTrend = isUptrend
      ? next.close > current.close
      : next.close < current.close;

    if (isCurrentTrend) {
      consecutiveCount++;
      startIndex = i;
    } else {
      // 趋势中断，停止查找
      break;
    }
  }

  // 获取连续趋势的起始数据
  const startOHLC = dataToAnalyze[startIndex];
  const startPrice = startOHLC.close;
  const endPrice = latestData.close;

  // 计算总变化幅度（百分比）
  const totalChangePercent = ((endPrice - startPrice) / startPrice) * 100;

  // 计算连续趋势天数
  const trendDays = consecutiveCount + 1;

  return {
    trendType,
    startTime: startOHLC.time,
    startOHLC,
    totalChangePercent: Number(totalChangePercent.toFixed(2)),
    trendDays,
    startPrice,
    endPrice,
  };
};

/**
 * 格式化趋势分析结果为可读字符串
 *
 * @param result - 趋势分析结果
 * @returns 格式化的字符串
 *
 * @example
 * ```typescript
 * const result = trendAnalysisCalc(ohlcData);
 * const formatted = formatTrendAnalysisResult(result);
 * console.log(formatted);
 * // "The stock has been in an uptrend since 2025-01-16, with a total price change of 2.94% during this period."
 * ```
 */
export const formatTrendAnalysisResult = (
  result: TrendAnalysisResult,
): string => {
  const date = new Date(result.startTime);
  const dateString = date.toISOString().split("T")[0]; // YYYY-MM-DD格式

  const sign = result.totalChangePercent >= 0 ? "+" : "";

  return `The stock has been in an ${result.trendType} since ${dateString}, with a total price change of ${sign}${result.totalChangePercent}% during this period.`;
};

/**
 * 验证OHLC数据的有效性
 *
 * @param ohlcData - OHLC数据
 * @returns 验证结果
 */
export const validateOHLCData = (
  ohlcData: OHLCData[],
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!Array.isArray(ohlcData)) {
    errors.push("OHLC数据必须是数组");
    return { isValid: false, errors };
  }

  if (ohlcData.length === 0) {
    errors.push("OHLC数据不能为空");
    return { isValid: false, errors };
  }

  if (ohlcData.length < 2) {
    errors.push("OHLC数据至少需要2个数据点");
    return { isValid: false, errors };
  }

  // 验证每个数据点的结构
  ohlcData.forEach((data, index) => {
    if (!data || typeof data !== "object") {
      errors.push(`第${index + 1}个数据点不是有效对象`);
      return;
    }

    if (typeof data.time !== "number" || data.time <= 0) {
      errors.push(`第${index + 1}个数据点的时间戳无效`);
    }

    if (typeof data.open !== "number" || data.open < 0) {
      errors.push(`第${index + 1}个数据点的开盘价无效`);
    }

    if (typeof data.high !== "number" || data.high < 0) {
      errors.push(`第${index + 1}个数据点的最高价无效`);
    }

    if (typeof data.low !== "number" || data.low < 0) {
      errors.push(`第${index + 1}个数据点的最低价无效`);
    }

    if (typeof data.close !== "number" || data.close < 0) {
      errors.push(`第${index + 1}个数据点的收盘价无效`);
    }

    // 验证价格逻辑关系
    if (data.high < data.low) {
      errors.push(`第${index + 1}个数据点的最高价不能低于最低价`);
    }

    if (data.high < data.open || data.high < data.close) {
      errors.push(`第${index + 1}个数据点的最高价必须大于等于开盘价和收盘价`);
    }

    if (data.low > data.open || data.low > data.close) {
      errors.push(`第${index + 1}个数据点的最低价必须小于等于开盘价和收盘价`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
